import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Repeat, Play, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const FREQ = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
const TYPE_LABELS = { expense: "Expense", income: "Income", saving: "Saving" };

export default function Recurring() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({ expense: [], income: [], saving: [] });
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [form, setForm] = useState({
    type: "expense", amount: "", category: "", note: "",
    frequency: "monthly", active: true, currency,
  });

  const load = async () => {
    const [{ data }, { data: cats }] = await Promise.all([
      api.get("/recurring"),
      api.get("/categories"),
    ]);
    setItems(data);
    setCategories(cats);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recurring", {
        ...form,
        amount: Number(form.amount),
        start_date: format(startDate, "yyyy-MM-dd"),
      });
      toast.success("Recurring rule added");
      setOpen(false);
      setForm({ type: "expense", amount: "", category: "", note: "", frequency: "monthly", active: true, currency });
      setStartDate(new Date());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create rule");
    }
  };

  const toggleActive = async (r) => {
    await api.patch(`/recurring/${r.id}`, { ...r, active: !r.active });
    load();
  };

  const runNow = async () => {
    const { data } = await api.post("/recurring/run");
    toast.success(`Ran recurring — ${data.created} transactions created`);
    load();
  };

  const remove = async () => {
    if (!deleteId) return;
    await api.delete(`/recurring/${deleteId}`);
    toast.success("Rule deleted");
    setDeleteId(null);
    load();
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Automation</div>
          <h1 className="font-display text-4xl mt-2">Recurring</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">Auto-log salaries, subscriptions and regular expenses.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="recurring-run-btn" variant="outline" onClick={runNow} className="rounded-full h-11 px-5">
            <Play className="w-4 h-4 mr-2" /> Run now
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-recurring-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5">
                <Plus className="w-4 h-4 mr-2" /> New rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Recurring rule</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: "" })}>
                      <SelectTrigger data-testid="recurring-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(FREQ).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      data-testid="recurring-amount"
                      type="number" step="0.01" min="0.01" required
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Start date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(startDate, "MMM d, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="recurring-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>
                      {(categories[form.type] || []).map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          <span className="inline-flex items-center gap-2"><span style={{ color: c.color }}>{c.icon}</span>{c.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Input
                    data-testid="recurring-note"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    data-testid="recurring-submit"
                    type="submit"
                    disabled={!form.amount || !form.category}
                    className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                  >
                    Add rule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div data-testid="recurring-empty" className="card-flat p-12 text-center">
          <Repeat className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No recurring rules</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Add one to auto-track salaries or subscriptions.</p>
        </div>
      ) : (
        <div data-testid="recurring-list" className="card-flat divide-y divide-[var(--divider)]">
          {items.map((r) => (
            <div key={r.id} data-testid={`recurring-row-${r.id}`} className="flex items-center gap-4 p-4">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                r.type === "income" ? "bg-[#EAF4EE] text-[#4C956C]" :
                r.type === "expense" ? "bg-[#FCE7E9] text-[#E63946]" :
                "bg-[#F5EAE3] text-[#E07A5F]"
              }`}>
                <Repeat className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.category} · <span className="text-[var(--muted-text)] capitalize">{r.type}</span></div>
                <div className="text-xs text-[var(--muted-text)]">
                  {FREQ[r.frequency]} · next {formatDate(r.next_run)}
                </div>
              </div>
              <div className={`font-mono-data text-sm ${r.type === "income" ? "text-[#4C956C]" : ""}`}>
                {r.type === "income" ? "+" : "−"}{formatCurrency(r.amount, r.currency)}
              </div>
              <Switch
                data-testid={`recurring-active-${r.id}`}
                checked={r.active}
                onCheckedChange={() => toggleActive(r)}
              />
              <Button
                data-testid={`recurring-delete-${r.id}`}
                size="icon" variant="ghost" onClick={() => setDeleteId(r.id)}
              >
                <Trash2 className="w-4 h-4 text-[#E63946]" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete recurring rule?</AlertDialogTitle>
            <AlertDialogDescription>Past auto-generated transactions won&apos;t be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="recurring-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
